import React, { useEffect, useState } from 'react';
import { SaveIcon, GlobeIcon, ClockIcon, InfoIcon, UploadCloudIcon } from 'lucide-react';
export function CurrencyDetail({
  currency,
  onSave,
  onCancel,
  isCreating,
  locale
}) {
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({
    currencyId: '',
    code: '',
    numericCode: '',
    name: '',
    symbol: '',
    countries: [],
    isDefault: false,
    decimalPlaces: 2
  });
  // Sample history data - in real app, this would come from API
  const [history, setHistory] = useState([{
    id: 1,
    eventType: 'updated',
    eventDescription: 'Currency symbol changed from "$" to "A$"',
    timestamp: '2026-01-19T20:02:52Z',
    userId: 'user-123',
    userName: 'John Smith',
    changes: [{
      field: 'symbol',
      oldValue: '$',
      newValue: 'A$'
    }]
  }, {
    id: 2,
    eventType: 'created',
    eventDescription: 'Currency created',
    timestamp: '2026-01-14T22:25:08Z',
    userId: 'user-456',
    userName: 'Jane Doe',
    changes: []
  }]);
  // Common country options for dropdown
  const commonCountries = ['United States', 'United Kingdom', 'Germany', 'France', 'Italy', 'Spain', 'Japan', 'China', 'India', 'Canada', 'Australia', 'Brazil', 'Russia', 'South Africa', 'Mexico', 'Singapore'];
  useEffect(() => {
    if (currency && !isCreating) {
      setFormData({
        ...currency,
        countries: [...currency.countries]
      });
    } else {
      setFormData({
        currencyId: '',
        code: '',
        numericCode: '',
        name: '',
        symbol: '',
        countries: [],
        isDefault: false,
        decimalPlaces: 2
      });
    }
  }, [currency, isCreating]);
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleNumericChange = e => {
    const {
      name,
      value
    } = e.target;
    const numericValue = parseInt(value, 10);
    if (!isNaN(numericValue)) {
      setFormData({
        ...formData,
        [name]: numericValue
      });
    } else if (value === '') {
      setFormData({
        ...formData,
        [name]: ''
      });
    }
  };
  const handleCountrySelect = e => {
    const selectedCountry = e.target.value;
    if (selectedCountry && !formData.countries.includes(selectedCountry)) {
      setFormData({
        ...formData,
        countries: [...formData.countries, selectedCountry]
      });
    }
  };
  const handleRemoveCountry = country => {
    setFormData({
      ...formData,
      countries: formData.countries.filter(c => c !== country)
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    onSave(formData);
  };
  const formatTimestamp = timestamp => {
    const date = new Date(timestamp);
    return date.toLocaleString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };
  const getEventIcon = eventType => {
    switch (eventType) {
      case 'created':
        return <UploadCloudIcon className="h-5 w-5" data-id="element-15698" />;
      case 'updated':
        return <InfoIcon className="h-5 w-5" data-id="element-15699" />;
      default:
        return <InfoIcon className="h-5 w-5" data-id="element-15700" />;
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-15701">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center border-b border-gray-200" data-id="element-15702">
        <div data-id="element-15703">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-15704">
            {isCreating ? 'Add New Currency' : `${formData.name} (${formData.code})`}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-15705">
            {isCreating ? 'Enter details for the new currency' : 'View and update currency details'}
          </p>
        </div>
        {activeTab === 'details' && <div className="flex space-x-3" data-id="element-15706">
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15707">
              Cancel
            </button>
            <button type="button" onClick={handleSubmit} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15708">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-15709" />
              Save Currency
            </button>
          </div>}
      </div>

      {/* Tabs */}
      {!isCreating && <div className="border-b border-gray-200" data-id="element-15710">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs" data-id="element-15711">
            <button onClick={() => setActiveTab('details')} className={`${activeTab === 'details' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-15712">
              Details
            </button>
            <button onClick={() => setActiveTab('relationships')} className={`${activeTab === 'relationships' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-15713">
              Relationships
            </button>
            <button onClick={() => setActiveTab('history')} className={`${activeTab === 'history' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-15714">
              History
            </button>
          </nav>
        </div>}

      {/* Tab Content */}
      <div className="px-4 py-5 sm:px-6" data-id="element-15715">
        {/* Details Tab */}
        {activeTab === 'details' && <form className="space-y-6" onSubmit={handleSubmit} data-id="element-15716">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-15717">
              {/* Basic Information */}
              <div className="sm:col-span-6" data-id="element-15718">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-15719">
                  Basic Information
                </h4>
              </div>
              <div className="sm:col-span-3" data-id="element-15720">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-15721">
                  Currency Name
                </label>
                <div className="mt-1" data-id="element-15722">
                  <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-15723" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-15724">
                <label htmlFor="symbol" className="block text-sm font-medium text-gray-700" data-id="element-15725">
                  Symbol
                </label>
                <div className="mt-1" data-id="element-15726">
                  <input type="text" name="symbol" id="symbol" value={formData.symbol} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-15727" />
                </div>
              </div>
              {/* ISO Codes */}
              <div className="sm:col-span-6" data-id="element-15728">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-15729">
                  ISO Codes
                </h4>
              </div>
              <div className="sm:col-span-3" data-id="element-15730">
                <label htmlFor="code" className="block text-sm font-medium text-gray-700" data-id="element-15731">
                  Currency Code (ISO 4217)
                </label>
                <div className="mt-1" data-id="element-15732">
                  <input type="text" name="code" id="code" value={formData.code} onChange={handleChange} maxLength={3} placeholder="XXX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md uppercase" required disabled={!isCreating} data-id="element-15733" />
                </div>
                {!isCreating && <p className="mt-1 text-xs text-gray-500" data-id="element-15734">
                    Currency code cannot be changed after creation
                  </p>}
              </div>
              <div className="sm:col-span-3" data-id="element-15735">
                <label htmlFor="numericCode" className="block text-sm font-medium text-gray-700" data-id="element-15736">
                  Numeric Code (ISO 4217)
                </label>
                <div className="mt-1" data-id="element-15737">
                  <input type="text" name="numericCode" id="numericCode" value={formData.numericCode} onChange={handleChange} maxLength={3} placeholder="XXX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-15738" />
                </div>
              </div>
              {/* Formatting */}
              <div className="sm:col-span-6" data-id="element-15739">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-15740">
                  Formatting
                </h4>
              </div>
              <div className="sm:col-span-3" data-id="element-15741">
                <div className="flex items-center h-full pt-6" data-id="element-15742">
                  <input id="isDefault" name="isDefault" type="checkbox" checked={formData.isDefault} onChange={handleChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-15743" />
                  <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700" data-id="element-15744">
                    Set as default currency
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500 pl-6" data-id="element-15745">
                  {formData.isDefault ? 'This will be the default currency for new transactions' : 'Check to make this the default currency'}
                </p>
              </div>
              {/* Countries */}
              <div className="sm:col-span-6" data-id="element-15746">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-15747">
                  Countries Using This Currency
                </h4>
              </div>
              <div className="sm:col-span-3" data-id="element-15748">
                <label htmlFor="countrySelect" className="block text-sm font-medium text-gray-700" data-id="element-15749">
                  Add Country
                </label>
                <div className="mt-1" data-id="element-15750">
                  <select id="countrySelect" name="countrySelect" value="" onChange={handleCountrySelect} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15751">
                    <option value="" data-id="element-15752">Select a country</option>
                    {commonCountries.map(country => <option key={country} value={country} data-id="element-15753">
                        {country}
                      </option>)}
                    <option value="Other" data-id="element-15754">Other (type manually)</option>
                  </select>
                </div>
              </div>
              {/* Custom country input */}
              {formData.countries.includes('Other') && <div className="sm:col-span-3" data-id="element-15755">
                  <label htmlFor="customCountry" className="block text-sm font-medium text-gray-700" data-id="element-15756">
                    Custom Country Name
                  </label>
                  <div className="mt-1 flex" data-id="element-15757">
                    <input type="text" id="customCountry" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Enter country name" data-id="element-15758" />
                    <button type="button" className="ml-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={() => {
                const input = document.getElementById('customCountry');
                if (input.value) {
                  setFormData({
                    ...formData,
                    countries: [...formData.countries.filter(c => c !== 'Other'), input.value]
                  });
                  input.value = '';
                }
              }} data-id="element-15759">
                      Add
                    </button>
                  </div>
                </div>}
              {/* Selected countries */}
              <div className="sm:col-span-6" data-id="element-15760">
                <div className="mt-1" data-id="element-15761">
                  {formData.countries.length === 0 ? <p className="text-sm text-gray-500" data-id="element-15762">
                      No countries selected. Please add at least one country
                      that uses this currency.
                    </p> : <div className="flex flex-wrap gap-2" data-id="element-15763">
                      {formData.countries.map(country => <div key={country} className="inline-flex items-center px-2.5 py-1.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-15764">
                          <GlobeIcon className="h-3 w-3 mr-1" data-id="element-15765" />
                          {country}
                          <button type="button" className="ml-1 text-indigo-600 hover:text-indigo-900 focus:outline-none" onClick={() => handleRemoveCountry(country)} data-id="element-15766">
                            &times;
                          </button>
                        </div>)}
                    </div>}
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-5 border-t border-gray-200" data-id="element-15767">
              <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15768">
                Cancel
              </button>
              <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15769">
                <SaveIcon className="h-4 w-4 mr-2" data-id="element-15770" />
                {isCreating ? 'Create Currency' : 'Update Currency'}
              </button>
            </div>
          </form>}

        {/* Relationships Tab */}
        {activeTab === 'relationships' && <div className="py-4" data-id="element-15771">
            <div className="text-center py-12" data-id="element-15772">
              <GlobeIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-15773" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-15774">
                No relationships
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-15775">
                Currency relationships will be displayed here.
              </p>
            </div>
          </div>}

        {/* History Tab */}
        {activeTab === 'history' && <div className="py-4" data-id="element-15776">
            <div className="bg-white rounded-lg border border-gray-200" data-id="element-15777">
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-15778">
                <h3 className="text-base font-semibold text-gray-900" data-id="element-15779">
                  Currency History
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-15780">
                  {history.length} events
                </p>
              </div>

              <div className="px-6 py-6" data-id="element-15781">
                {history.length === 0 ? <div className="text-center py-12" data-id="element-15782">
                    <ClockIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-15783" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-15784">
                      No history
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-15785">
                      No changes have been recorded for this currency yet.
                    </p>
                  </div> : <div className="flow-root" data-id="element-15786">
                    <ul className="-mb-8" data-id="element-15787">
                      {history.map((event, eventIdx) => <li key={event.id} data-id="element-15788">
                          <div className="relative pb-8" data-id="element-15789">
                            {eventIdx !== history.length - 1 && <span className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" data-id="element-15790" />}
                            <div className="relative flex items-start space-x-3" data-id="element-15791">
                              <div data-id="element-15792">
                                <div className="relative px-1" data-id="element-15793">
                                  <div className="h-10 w-10 bg-gray-100 rounded-full ring-8 ring-white flex items-center justify-center" data-id="element-15794">
                                    {getEventIcon(event.eventType)}
                                  </div>
                                </div>
                              </div>
                              <div className="min-w-0 flex-1" data-id="element-15795">
                                <div data-id="element-15796">
                                  <div className="text-sm font-medium text-gray-900" data-id="element-15797">
                                    {event.eventDescription}
                                  </div>
                                  <p className="mt-0.5 text-sm text-gray-500" data-id="element-15798">
                                    {formatTimestamp(event.timestamp)}
                                  </p>
                                </div>
                                {event.changes && event.changes.length > 0 && <div className="mt-2 text-sm text-gray-700" data-id="element-15799">
                                    <div className="bg-gray-50 rounded-md p-3 space-y-1" data-id="element-15800">
                                      {event.changes.map((change, idx) => <div key={idx} className="flex items-center text-xs" data-id="element-15801">
                                          <span className="font-medium text-gray-700" data-id="element-15802">
                                            {change.field}:
                                          </span>
                                          <span className="ml-2 text-gray-500 line-through" data-id="element-15803">
                                            {change.oldValue}
                                          </span>
                                          <span className="mx-2 text-gray-400" data-id="element-15804">
                                            →
                                          </span>
                                          <span className="text-gray-900" data-id="element-15805">
                                            {change.newValue}
                                          </span>
                                        </div>)}
                                    </div>
                                  </div>}
                              </div>
                            </div>
                          </div>
                        </li>)}
                    </ul>
                  </div>}
              </div>
            </div>
          </div>}
      </div>
    </div>;
}